import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type EdgeData,
  isNonNullable,
  isPathLookupEdgeData,
  isTopologyEdgeData,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { canOpenDeviceEdgeEntity } from 'components/DeviceView/helpers/canOpenDeviceEdgeEntity';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { useLocale } from 'containers/App/LocaleContext';
import type { FC } from 'react';
import { closeEdgeContextMenu } from './commands/edges/closeEdgeContextMenu';
import { showEdgeDetail } from './commands/showEdgeDetail';

import { ContextMenu } from 'components/DropdownMenu/ContextMenu';
import { showPacketDetail } from 'components/Graph/commands/pathLookup/showPacketDetail';

interface Props {
  container: HTMLElement | null;
}

const hasEdgeDetail = (edge: EdgeData): boolean =>
  (isTopologyEdgeData(edge) && canOpenDeviceEdgeEntity(edge.protocol)) ||
  (isPathLookupEdgeData(edge) && isNonNullable(edge.intentCheckResult));
const hasEdgePacketDetail = (edge: EdgeData): boolean =>
  isPathLookupEdgeData(edge);

export const GraphEdgeContextMenu: FC<Props> = (props) => {
  const {
    canvasGraph: { graphEdgeContextMenu: msgGraphEdgeContextMenu },
  } = useLocale();

  const ctxProps = useGraphContextSelectors({
    edges: prop('graph', 'edges'),
    dispatch: prop('dispatch'),
    edgeContextMenu: prop('edgeContextMenu'),
  });

  if (ctxProps.edgeContextMenu === null) {
    stumble('Edge context menu has to be active in state');
    return null;
  }

  const { edgeId } = ctxProps.edgeContextMenu;
  // Fixed noUncheckedIndexedAccess NIM-19408
  const edge = ctxProps.edges[edgeId]!;
  const hasDetail = hasEdgeDetail(edge);

  return (
    <ContextMenu
      container={props.container}
      onClose={() => {
        ctxProps.dispatch(closeEdgeContextMenu);
      }}
      position={ctxProps.edgeContextMenu.position}
      items={[
        (isTopologyEdgeData(edge) || hasDetail) && {
          disabled: !hasDetail,
          label: msgGraphEdgeContextMenu.showDetail,
          onClick: () => {
            ctxProps.dispatch(showEdgeDetail, { edgeId });
          },
        },
        hasEdgePacketDetail(edge) && {
          label: msgGraphEdgeContextMenu.showPacketDetail,
          onClick: () => {
            ctxProps.dispatch(showPacketDetail, { packetEdgeId: edgeId });
          },
        },
      ]}
    />
  );
};
